"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = void 0;
const papaparse_1 = __importDefault(require("papaparse"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const consolidateErrors_1 = require("./consolidateErrors");
const errorUtils_1 = require("./errorUtils");
const utils_1 = require("./utils");
const consolidateFormulaErrors = (formulaErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(formulaErrors, (error) => error.formula, // Group by formula
    (error) => ({
        message: error.message,
        row: String(error.row),
    }), // Map to row data
    (formula) => `Formula [${formula}] has errors`);
};
const consolidateFilterValidationErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.message, // Group by message
    (error) => ({
        row: String(error.row),
    }), // Map to row data
    (message) => `${message}`);
};
const consolidateToStructuredErrors = (errors) => {
    const unknownErrors = errors.filter((error) => error.type === 'filterValidation');
    const formulaErrors = errors.filter((error) => error.type === 'filterFormulaEvaluationError');
    // we want to consolidate the errors if there are too many of those.
    return [
        ...consolidateFormulaErrors(formulaErrors),
        ...consolidateFilterValidationErrors(unknownErrors),
    ];
};
const checkPreErrors = (transformation) => {
    // At the moment, just checking against formulas and available formulas
    // for FormulaEngine usage when assessing output dataTape
    const { filterFormula, srcDataTape } = transformation;
    const errors = [];
    const availableVariables = new Set();
    for (const { field } of srcDataTape.baseFields) {
        availableVariables.add(field);
    }
    const { errorMessages } = (0, formula_engine_1.getFormulaErrors)(filterFormula, availableVariables);
    for (const message of errorMessages) {
        errors.push({ type: 'metadataValidation', message });
    }
    return errors;
};
const validateTransform = (transformation) => {
    const preErrors = checkPreErrors(transformation);
    if (preErrors.length) {
        return {
            isValid: false,
            errors: preErrors,
        };
    }
    // Since only just filtering the rows down and not impacting
    // any actual fields or values, can just return provided srcDataTape
    return {
        isValid: true,
        data: transformation.srcDataTape,
    };
};
const processFilter = async (params) => {
    const { formulaEngine, transformation, inputCsvString } = params;
    const baseParseConfig = {
        ...common_1.csvConversion.NEWLINE_PARSE_CONFIG,
        delimiter: ',',
        quoteChar: '"',
        escapeChar: '"',
        header: true,
        skipEmptyLines: true,
    };
    // Set up functionality to stream the CSV string to help manage data sizing
    // This provides us additional flexibility and configuration as "papaparse"
    // also gives us ability to utilize worker threads, if needed
    const csvStreamPromise = new Promise((resolve) => {
        const outputData = [];
        const outputErrors = [];
        papaparse_1.default.parse(inputCsvString, {
            ...baseParseConfig,
            // This function will be called for each line in the file
            step(result, parser) {
                const { data, errors, meta } = result;
                if (errors.length) {
                    for (const { message, row } of errors) {
                        outputErrors.push({
                            type: 'filterValidation',
                            message,
                            row,
                        });
                    }
                    resolve({ outputData, outputErrors });
                    parser.abort(); // stop the stream since there's an error
                    return;
                }
                // Set variables for row of data that is being focused on
                // Then run provided filter formula for that data row to evaluate
                // if it should still be added (passes filter)
                try {
                    formulaEngine.setVariables(data);
                    const isDataFilterCompliant = formulaEngine.evaluateBoolean(transformation.filterFormula);
                    if (isDataFilterCompliant) {
                        outputData.push(data);
                    }
                }
                catch (err) {
                    const formulaEvaluationError = {
                        type: 'filterFormulaEvaluationError',
                        formula: transformation.filterFormula,
                        message: (0, common_1.getErrorMessage)(err),
                        row: meta.cursor,
                    };
                    outputErrors.push(formulaEvaluationError);
                    (0, common_1.debugLog)(formulaEvaluationError, err);
                }
            },
            complete() {
                resolve({ outputData, outputErrors });
            },
        });
    });
    const { outputData, outputErrors } = await csvStreamPromise;
    return { errors: outputErrors, outputData };
};
const performTransform = async (params) => {
    try {
        const { dataTapeBuffers, transformation } = params;
        (0, common_1.finleyAssert)(dataTapeBuffers.length === 1, 'filter: Expected 1 data tape buffer');
        const srcDataTapeBuffer = dataTapeBuffers[0];
        const inputCsvString = common_1.finleyBuffer.toString(srcDataTapeBuffer);
        const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, [transformation.filterFormula], undefined);
        const { errors, outputData } = await processFilter({
            transformation,
            inputCsvString,
            formulaEngine,
        });
        if (errors.length) {
            return {
                isValid: false,
                errors: consolidateToStructuredErrors(errors),
            };
        }
        const tapeDefinitionResult = validateTransform(transformation);
        (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, 'Invalid transformation');
        const outputDataTape = tapeDefinitionResult.data;
        (0, common_1.finleyAssert)(outputDataTape, 'Output data tape is not defined');
        const outputBuffer = (0, utils_1.rowDataToCsv)(outputData, outputDataTape);
        return { isValid: true, data: { outputBuffer }, supplementals: [] };
    }
    catch (error) {
        return (0, errorUtils_1.convertErrorToTapeFailureResult)(error, 'filter: ');
    }
};
exports.transformOperation = {
    operation: 'filter',
    validateTransform,
    performTransform,
};
//# sourceMappingURL=filter-transform.js.map